import {AddressResponseData, IAddressData} from "@connox/typescript-library";
import * as React from "react";
import {useTranslation} from "react-i18next";
import {useCartContext} from "../../../cart/CartContextProvider/CartContextProvider";
import {Button, ButtonStyles, ButtonType} from "../../../lib/form/Button/Button";
import Spinner from "../../../utils/loader/Spinner";
import {LoginMode} from "../../Checkout";
import {useCheckoutContext} from "../../CheckoutContextProvider/CheckoutContextProvider";
import {MaxLengthInput} from "../MaxLengthInput";
import {isSameAddresses} from "../AddressStep/AddressStep";

interface IForm extends HTMLFormElement {
  IsGuest: HTMLInputElement;
  BusinessCustomer: HTMLInputElement;
  Email: HTMLInputElement;
  FirstName: HTMLInputElement;
  LastName: HTMLInputElement;
  Gender: HTMLInputElement;
}

const NewCustomer: React.FC = () => {
  const {checkout} = useCheckoutContext();
  const {setViewState} = useCartContext();
  const {t} = useTranslation("checkout");
  // if partial already in invoice, user formerly tried to register
  const emailForPartialAddress = checkout.invoiceAddress?.email || "";
  const firstNameForPartialAddress = checkout.invoiceAddress?.firstName;
  const lastNameForPartialAddressl = checkout.invoiceAddress?.lastName;
  const genderForPartialAddress = checkout.invoiceAddress?.gender || "";
  const buttonText = t("Weiter");
  const [{buttonState, errors}, setButtonState] = React.useState<{
    buttonState: string | JSX.Element;
    errors?: {
      firstNameError: string;
      lastNameError: string;
      emailError: string;
      genderError: string;
    };
  }>({buttonState: buttonText});
  const {isGuest} = checkout;
  const {isCorporateCustomer} = checkout.cartData;

  const handleStartRegisterButton = async (evt: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    evt.preventDefault();
    setButtonState({buttonState: <Spinner />});
    const button = evt.target as HTMLButtonElement;
    const form = button.form as IForm;
    const isGuestCheckboxSet = form.IsGuest.checked;
    const isCorporateCheckboxSet = form.BusinessCustomer.checked;
    /**
     * Subset of address fields user has to
     * fill as new customer in login step.
     * Will then be written into address step form
     * fields and user has to fill in the rest
     */
    const partialAddress: Pick<IAddressData, "email" | "firstName" | "gender" | "lastName"> = {
      email: form.Email.value,
      firstName: form.FirstName.value,
      gender: form.Gender.value as "M" | "F",
      lastName: form.LastName.value,
    };
    const {email, firstName, gender, lastName} = partialAddress;
    if (!(email && !form.Email.validity.typeMismatch && firstName && gender && lastName)) {
      const firstNameError = form.FirstName.value ? "" : " invalid";
      const emailError = form.Email.value && !form.Email.validity.typeMismatch ? "" : " invalid";
      const genderError = form.Gender.value ? "" : " invalid";
      const lastNameError = form.LastName.value ? "" : " invalid";
      setButtonState({buttonState: buttonText, errors: {emailError, firstNameError, genderError, lastNameError}});

      return;
    }

    // fill fields from new customer step into
    // whats already present in checkout address
    const completeInvoiceAddress = inflateAddress(
      partialAddress,
      checkout.invoiceAddress,
      checkout.cartData.countryCode,
    );

    let completeDeliveryAddress: IAddressData;

    if (checkout.deliveryAddress === null || isSameAddresses(checkout.invoiceAddress, checkout.deliveryAddress)) {
      // if same addresses, including both null
      completeDeliveryAddress = completeInvoiceAddress;
    } else {
      // not empty and differing to invoice
      completeDeliveryAddress = checkout.deliveryAddress as IAddressData;
    }

    setViewState({
      isRegistering: true,
      partials: {partialAddress, isGuest: isGuestCheckboxSet, isCorporateCustomer: isCorporateCheckboxSet},
    });
    await checkout.put({
      address: {
        delivery: completeDeliveryAddress,
        invoice: completeInvoiceAddress,
      },
      cart: {
        isCorporateCustomer: isCorporateCheckboxSet,
      },
      isGuest: isGuestCheckboxSet,
    });
  };

  return (
    <div className="NewCustomer">
      <h1>{t("Neu bei Connox?")}</h1>
      <form action="/warenkorb/kasse.html">
        <input type="hidden" name="c" value="checkout" />
        <input type="hidden" name="a" value="invoice_address" />
        <input type="hidden" name="IsAlsoDeliveryAddress" value="True" />
        <input type="hidden" name="Type" value="InvoiceAddress" />
        <MaxLengthInput
          className={"Register" + (errors?.firstNameError || "")}
          defaultValue={firstNameForPartialAddress}
          hintText={t("Bitte geben Sie einen Vornamen an")}
          type="text"
          name="FirstName"
          placeholder={t("Ihr Vorname *")}
        />
        <MaxLengthInput
          className={"Register" + (errors?.lastNameError || "")}
          defaultValue={lastNameForPartialAddressl}
          hintText={t("Bitte geben Sie einen Nachnamen an")}
          type="text"
          name="LastName"
          placeholder={t("Ihr Nachname *")}
        />
        <p className={"Genders Register" + (errors?.genderError || "")}>
          <label>
            <input type="radio" id="f" name="Gender" value="F" defaultChecked={genderForPartialAddress === "F"} />
            {t("Weiblich")}
          </label>
          <label>
            <input type="radio" id="m" name="Gender" value="M" defaultChecked={genderForPartialAddress === "M"} />
            {t("Männlich")}
          </label>
        </p>
        <span className="InvalidInput">{t("Bitte geben Sie Ihr Geschlecht an")}</span>
        <MaxLengthInput
          className={"Register" + (errors?.emailError || "")}
          defaultValue={emailForPartialAddress}
          hintText={t("Bitte geben Sie eine gültige E-Mail-Adresse an")}
          type="email"
          placeholder={t("Ihre E-Mail-Adresse *")}
          name="Email"
        />
        <p style={{fontSize: ".875rem"}}>{t("* Pflichtfelder")}</p>
        <div className="BusinessCheck">
          <input type="checkbox" id="BusinessCustomer" name="BusinessCustomer" defaultChecked={isCorporateCustomer} />
          <label htmlFor="BusinessCustomer">{t("Ich möchte als Geschäftskunde einkaufen")}</label>
        </div>
        <div className="GuestCheck">
          <input type="checkbox" id="IsGuest" name="IsGuest" defaultChecked={isGuest} />
          <label htmlFor="IsGuest">{t("Ich möchte als Gast einkaufen")}</label>
        </div>
        <Button
          btnIdentifier={LoginMode.NEWCUSTOMER}
          type={typeof buttonState === "string" ? ButtonType.default : ButtonType.disabled}
          action={handleStartRegisterButton}
          styles={[ButtonStyles.wide]}
        >
          {buttonText}
        </Button>
      </form>
    </div>
  );
};

interface IPartialAdress {
  email: string;
  firstName: string;
  gender: string;
  lastName: string;
}

const inflateAddress = (
  partialAddress: IPartialAdress,
  inputAddress: AddressResponseData | null,
  countryCode: string,
): IAddressData => {
  if (!inputAddress) {
    return {
      ...partialAddress,
      city: "",
      company: "",
      countryCode,
      dateOfBirth: null,
      department: "",
      gender: partialAddress.gender as "M" | "F",
      number: "",
      phone: "",
      state: "",
      street: "",
      title: "",
      zip: "",
    };
  }

  return {
    ...partialAddress,
    city: inputAddress.city,
    company: inputAddress.company,
    countryCode: inputAddress.countryCode,
    dateOfBirth: inputAddress.dateOfBirth,
    department: inputAddress.department,
    gender: partialAddress.gender as "M" | "F",
    number: inputAddress.number,
    phone: inputAddress.phone,
    state: inputAddress.state,
    street: inputAddress.street,
    title: inputAddress.title,
    zip: inputAddress.zip,
  };
};

export default NewCustomer;
